import * as React from 'react';
import styled, { css } from 'styled-components';
import PhotoCard from '@components/photo-card/PhotoCard';
import AchievementBadge from '@components/4cut-ranking/AchievementBadge';
import Text from '@components/common/Text';
import { colorCode } from '@styles/styleUtils';
import { showAchievementBottomSheet } from '@components/bottomSheet/AchievementBottomSheet';
import Icon from '@src/svg/Icon';

type PhotoCardSizeType = 'XS' | 'S' | 'M' | 'L';

const OverlapStyle = {
  XS: css`
    margin: 4px 4px 5px;
    border-radius: 10px 10px 14px 14px;
  `,
  S: css`
    margin: 4px;
    border-radius: 10px 10px 14px 14px;
  `,
  M: css`
    margin: 6px;
    border-radius: 13px 13px 18px 18px;
  `,
  L: css`
    margin: 6px;
    border-radius: 13px 13px 18px 18px;
  `,
};

const OverlapWrap = styled.div<{
  size: PhotoCardSizeType;
}>`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0) 75%,
    rgba(17, 17, 17, 0.5) 100%
  );
  ${({ size }) => OverlapStyle[size]};
  display: flex;
  margin-top: auto;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0 6px 12px 12px;
  z-index: ${({ theme }) => theme.zIndex.photoCard.dimmed};

  > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: left;

    > label {
      width: fit-content;
      background: #2c2c34;
      border: 1px solid #8f5ee9;
      color: ${colorCode('dark.lightPurple01')};
      padding: 4px 8px 4px 6px;
      border-radius: 99px;
      display: flex;
      align-items: center;
      gap: 2px;
    }
  }
`;

interface Props {
  photoCardInfo?: PhotoCardType;
  size?: PhotoCardSizeType;
  handleClick?: () => void;
  dimmed?: boolean;
  lazy?: boolean;
  isAnimated?: boolean;
  hasAchievementInfoBottomSheet?: boolean;
  enableZoom?: boolean;
}

const RegionLabelPhotoCard: React.FC<
  Props & React.HTMLAttributes<HTMLDivElement>
> = ({
  photoCardInfo,
  size = 'L',
  handleClick,
  dimmed = false,
  lazy = true,
  isAnimated = false,
  hasAchievementInfoBottomSheet = false,
  enableZoom,
  ...rest
}) => {
  const handleClickAchievementBadge = async (
    e: React.MouseEvent<HTMLElement>,
  ) => {
    e.stopPropagation();

    if (hasAchievementInfoBottomSheet) {
      await showAchievementBottomSheet();
    }
  };

  return (
    <PhotoCard
      photoCardInfo={photoCardInfo}
      size={size}
      handleClick={handleClick}
      showBottomLogo={false}
      dimmed={dimmed}
      lazy={lazy}
      enableZoom={enableZoom}
      additionalContent={
        <OverlapWrap size={size}>
          <div>
            <label>
              <Icon name="pin" width={14} height={14} />
              <Text typo="caption2_medium" color="dark.lightPurple01">
                {photoCardInfo?.region || '기타'}
              </Text>
            </label>
            <Text typo="body1_semiBold" color="white">
              {photoCardInfo?.photoCardName}
            </Text>
          </div>
          {!!photoCardInfo?.winCount && (
            <AchievementBadge.Win
              count={photoCardInfo.winCount}
              isAnimated={isAnimated}
              onClick={handleClickAchievementBadge}
            />
          )}
          {photoCardInfo?.winCount === 0 && photoCardInfo?.enterSemiFinal && (
            <AchievementBadge.Semifinal
              size="small"
              isAnimated={isAnimated}
              onClick={handleClickAchievementBadge}
            />
          )}
        </OverlapWrap>
      }
      {...rest}
    />
  );
};

export default RegionLabelPhotoCard;
